import { FILE_TREE } from '@/constants'
import { SECTIONS } from '@/types/sections'
import {
  isNodeMimeTypeFolder,
  isNodeMimeTypeGeneric,
  isNodeMimeTypeInbox,
} from '@deepcloud/deep-ui-utils'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useSettingsStore } from '@/stores/settings/settings'
import { useDragDrop } from '@/composables/drag-drop'
import type { FileTree } from '@/types/file-tree'
import type { MaybeRefOrGetter } from 'vue'

export function useBoxNavigationDrawerDragDrop(options: {
  typeId: MaybeRefOrGetter<string | null>
  boxId: MaybeRefOrGetter<string | null>
  onExpandNode: (item: FileTree) => void
}) {
  const dragDrop = useDragDrop()
  const settingsStore = useSettingsStore()
  const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
  const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()

  const typeId = computed(() => toValue(options.typeId))
  const boxId = computed(() => toValue(options.boxId))

  function getElementById(nodeId: string) {
    const selector = document.querySelector(
      `.v-navigation-drawer__content [data-node-id='${nodeId}']`,
    )
    if (selector) {
      return selector as HTMLElement
    }

    return undefined
  }

  function getNodeAndParent(item: string | FileTree) {
    let element

    interface Output {
      node: FileTree | undefined
      parent: HTMLElement | undefined
    }

    const output: Output = {
      node: undefined,
      parent: undefined,
    }

    if (typeof item === 'string') {
      switch (item) {
        case SECTIONS.INBOX:
          output.node = deepBoxDeepBoxesBoxesNodesStore.rootNodes.queue as
            FileTree | undefined
          break
        case SECTIONS.FILES:
          output.node = deepBoxDeepBoxesBoxesNodesStore.rootNodes.files as
            FileTree | undefined
          break
        case SECTIONS.TRASH:
          output.node = deepBoxDeepBoxesBoxesNodesStore.rootNodes.trash as
            FileTree | undefined
          break

        // no default
      }
      element = getElementById(item)
    } else {
      output.node = item
      element = getElementById(item.nodeId)
    }

    // if the node is a folder, get its root parent element
    if (
      output.node?.mimeType &&
      (isNodeMimeTypeGeneric(output.node.mimeType) ||
        isNodeMimeTypeFolder(output.node.mimeType))
    ) {
      if (output.node?.nodeId) {
        output.parent = getElementById(output.node?.nodeId)
      }
    } else {
      // otherwise, use the element itself (files, inbox and trash nodes)
      output.parent = element
    }

    return output
  }

  const currentLongHoverTimeout = ref<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const currentLongHoverItem = ref<string | FileTree | null>(null)
  const longHoverDelay = 400

  function longHoverStart(item: string | FileTree) {
    const id = typeof item === 'string' ? item : item.nodeId
    const currentId =
      typeof currentLongHoverItem.value === 'string'
        ? currentLongHoverItem.value
        : currentLongHoverItem.value?.nodeId

    if (id === currentId) {
      return
    }

    currentLongHoverItem.value = item
    if (currentLongHoverTimeout.value) {
      clearTimeout(currentLongHoverTimeout.value)
    }
    currentLongHoverTimeout.value = setTimeout(
      expandAfterLongHover,
      longHoverDelay,
    )
  }

  function longHoverLeave() {
    currentLongHoverItem.value = null
    if (currentLongHoverTimeout.value) {
      clearTimeout(currentLongHoverTimeout.value)
    }
  }

  function expandAfterLongHover() {
    const item = currentLongHoverItem.value
    if (!item) return

    const id = typeof item === 'string' ? item : item.nodeId

    // Ignore long hover for inbox and trash elements
    if ([FILE_TREE.FILE_TREE_INBOX, FILE_TREE.FILE_TREE_TRASH].includes(id)) {
      return
    }

    if (typeof item === 'string') return

    options.onExpandNode(item)
  }

  async function dragOver(event: DragEvent, item: string | FileTree) {
    if (dragDrop.isNodeOverLocked.value) {
      return
    }

    dragDrop.isNodeOverLocked.value = true
    const { node, parent } = getNodeAndParent(item)
    dragDrop.isNodeOverLocked.value = false

    if (node && parent) {
      dragDrop.onDragOver(event, node, parent)
    }

    longHoverStart(item)
  }

  async function dragLeave(event: DragEvent, item: string | FileTree) {
    if (dragDrop.isNodeOverLocked.value) {
      return
    }

    dragDrop.isNodeOverLocked.value = true
    const { node, parent } = getNodeAndParent(item)
    dragDrop.isNodeOverLocked.value = false
    if (node && parent) {
      dragDrop.onDragLeave(event, node, parent)
    }

    longHoverLeave()
  }

  function dragStart(e: DragEvent, item: FileTree) {
    dragDrop.onDragStartNodes(e, [item], item.parentNodeId)
  }

  async function dropNode(event: DragEvent, item: string | FileTree) {
    if (dragDrop.isNodeOverLocked.value) {
      return
    }

    dragDrop.isNodeOverLocked.value = true
    const { node, parent } = getNodeAndParent(item)
    dragDrop.isNodeOverLocked.value = false
    if (parent) {
      parent.classList.remove(dragDrop.TREE_ITEM_HOVER_CLASS)
    }
    if (!node) return

    try {
      await dragDrop.onDropNode(event, node)
      // update BoxInfo `queueCount` if drop a node in the `inbox`
      if (isNodeMimeTypeInbox(node.mimeType) && typeId.value && boxId.value) {
        await deepBoxBoxInfoStore.updateBoxInfoQueueCounterByAPI(
          typeId.value,
          boxId.value,
        )
      }
    } finally {
      deepBoxDeepBoxesBoxesNodesStore.selectedNodeIds = []
    }
  }

  function onDragOver() {
    if (settingsStore.navDrawerMini) {
      settingsStore.navDrawerMini = false
    }
  }

  return {
    dragDrop,
    dragOver,
    dragLeave,
    dragStart,
    dropNode,
    onDragOver,
  }
}
